/**
 * Whether walking a bucket again could reach a different answer.
 *
 * "Walk it again" is the advice this product gives when nothing is confirmed,
 * and it was given unconditionally. A walk reads the same bytes with the rules
 * the collector carries, so re-running it against an unchanged rule set
 * produces the same dispositions it produced last time: the same files traced,
 * the same files left unrecognised. Telling an operator to repeat a job that
 * cannot change its own outcome wastes their time and their trust.
 *
 * What makes the advice true is a rule set that has grown since the walk
 * sealed. The backend registers every walker rule with the version and the
 * timestamp it was registered at, so this is a fact the product can check
 * rather than an assumption it has to make. It has been checkable all along;
 * nothing called the route.
 */

export interface RegisteredRule {
  /** When this rule entered the registry. */
  registeredAt: string;
  /** The collector version that registered it. */
  version: string;
}

export type RewalkVerdict =
  | { kind: 'WORTH_IT'; newRules: number; newestVersion: string }
  | { kind: 'SAME_RULES' }
  | { kind: 'UNKNOWN' };

/**
 * What another walk over this bucket could do, given when it last sealed.
 *
 * `UNKNOWN` is a real answer and the default: with no seal time or no rule
 * registry read, the honest position is that the product does not know, and it
 * must not present either verdict as though it did.
 */
export function rewalkVerdict(sealedAt: string | null, rules: RegisteredRule[] | null): RewalkVerdict {
  if (!sealedAt || !rules || rules.length === 0) return { kind: 'UNKNOWN' };
  const sealed = Date.parse(sealedAt);
  if (Number.isNaN(sealed)) return { kind: 'UNKNOWN' };

  const newer = rules.filter((r) => {
    const at = Date.parse(r.registeredAt);
    return !Number.isNaN(at) && at > sealed;
  });
  if (newer.length === 0) return { kind: 'SAME_RULES' };

  // The newest version among the rules that arrived after the seal, which is
  // what an operator would quote when asking why this walk differs.
  const newestVersion = newer
    .slice()
    .sort((a, b) => Date.parse(a.registeredAt) - Date.parse(b.registeredAt))
    .at(-1)!.version;
  return { kind: 'WORTH_IT', newRules: newer.length, newestVersion };
}

/** The verdict as one sentence, in the operator's terms. */
export function rewalkSentence(v: RewalkVerdict): string {
  if (v.kind === 'WORTH_IT')
    return `${v.newRules} ${v.newRules === 1 ? 'rule has' : 'rules have'} been registered since this bucket sealed, up to collector ${v.newestVersion}, so another walk can recognise files the last one could not.`;
  if (v.kind === 'SAME_RULES')
    return 'No rule has been registered since this bucket sealed, so another walk reads the same bytes with the same rules and reaches the same answer. A new rule is what changes that.';
  return 'Whether another walk would find anything new depends on the rule set having grown since this one sealed, which has not been read.';
}
